import { useCallback, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { AppUpdate, AppUpdateAvailability } from "@capawesome/capacitor-app-update";
import { api } from "./api";
import { getAppInfo } from "./appStore";

export interface UpdateState {
  available: boolean; // a newer version is on Google Play
  required: boolean; // this version is below the server's minimum
}

// Asks Google Play whether a newer build exists (so releasing in Play Console
// is all that is needed), and the server whether this build is too old to use.
// Checked at launch and every time the app comes back to the front.
export function useAppUpdate(): UpdateState {
  const [state, setState] = useState<UpdateState>({ available: false, required: false });

  const check = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return;
    let available = false;
    let required = false;
    try {
      const info = await getAppInfo();
      const cfg = await api.get("/api/config");
      if (info && Number(cfg?.minAppVersionCode) > info.build) required = true;
    } catch {
      // offline: leave as is
    }
    try {
      const u = await AppUpdate.getAppUpdateInfo();
      available = u.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE;
    } catch {
      // not installed from Google Play (a test build) or Play services missing
    }
    setState({ available, required });
  }, []);

  useEffect(() => {
    check();
    const sub = Capacitor.isNativePlatform() ? App.addListener("appStateChange", ({ isActive }) => { if (isActive) check(); }) : null;
    return () => { sub?.then((s) => s.remove()).catch(() => { }); };
  }, [check]);

  return state;
}
